import { LoadingOutlined } from '@ant-design/icons';
import { gql } from '@apollo/client';
import { Menu } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckIcon, CloseIcon } from 'components/Icons';
import { Button, MenuButton, useMessage } from 'components/ui';
import { DAMAGE_CONFIRM as DAMAGE_CONFIRM_RULE } from 'utils/access-control/rules';
import { useApolloContext, useMutation } from 'utils/apollo';
import { RenderWithAccess } from 'utils/usePermissions';

const SET_DAMAGE_CONFIRMED = gql`
  mutation ConfirmDamage($branchGlobalId: UUID!, $confirm: Boolean!) {
    confirmDamage(branchGlobalId: $branchGlobalId, confirm: $confirm) {
      id
      globalId
      branchGlobalId
      branchSlug
      version
      confirmed
    }
  }
`;

// For now have to support both PAPI & Atlas patterns.
export const ConfirmDamageToggle = ({
  damage: { id, isConfirmed: papiConfirmed = false, confirmed: atlasConfirmed = false },
  type,
  addLeftMargin = true,
  className = '',
}) => {
  const { t } = useTranslation();
  const message = useMessage();
  const { papiClient } = useApolloContext();
  const isConfirmed = papiConfirmed || atlasConfirmed;
  const [saving, setSaving] = useState(false);

  const [setConfirmDamage] = useMutation(SET_DAMAGE_CONFIRMED, {
    onCompleted({ confirmDamage: { branchSlug, version, confirmed } }) {
      const label = `${branchSlug.toUpperCase()}-${version}`;
      message.success(
        confirmed
          ? t('damage.damageConfirmedSuccess', 'Damage {{label}} confirmed.', { label })
          : t('damage.damageUnconfirmedSuccess', 'Damage {{label}} unconfirmed.', { label })
      );
    },
    update(
      _,
      {
        data: {
          confirmDamage: { confirmed },
        },
      }
    ) {
      // Have to reach over to the other apollo client cache for now since
      // this mutation happens on atlasClient but damages live in papiClient
      papiClient.writeFragment({
        id: `Damage:${id}`,
        fragment: gql`
          fragment DamageConfirmed on Damage {
            __typename
            isConfirmed
          }
        `,
        data: {
          isConfirmed: confirmed,
          __typename: 'Damage',
        },
      });
    },
  });

  const handleSetConfirm = async confirm => {
    setSaving(true);
    await setConfirmDamage({
      variables: {
        branchGlobalId: id,
        confirm,
      },
    });
    setSaving(false);
  };

  function render() {
    if (saving) {
      return type === 'menu' ? (
        <Menu.Item disabled>
          <LoadingOutlined style={{ marginRight: '0.5rem' }} />
          {t('components.saving', 'Saving…')}
        </Menu.Item>
      ) : (
        <Button disabled>
          {t('components.saving', 'Saving…')} <LoadingOutlined />
        </Button>
      );
    }

    if (type === 'menu') {
      return isConfirmed ? (
        <MenuButton icon={<CloseIcon />} type="link" onClick={() => handleSetConfirm(false)}>
          {t('damage.unconfirmDamage', 'Unconfirm Damage')}
        </MenuButton>
      ) : (
        <MenuButton
          icon={<CheckIcon addLeftMargin={addLeftMargin} className={className} />}
          type="link"
          onClick={() => handleSetConfirm(true)}
        >
          {t('damage.confirmDamage', 'Confirm Damage')}
        </MenuButton>
      );
    }

    return isConfirmed ? (
      <Button onClick={() => handleSetConfirm(false)}>
        <CloseIcon />
        {t('damage.unconfirmDamage', 'Unconfirm Damage')}
      </Button>
    ) : (
      <Button onClick={() => handleSetConfirm(true)} className={className}>
        <CheckIcon addLeftMargin={addLeftMargin} />
        {t('damage.confirmDamage', 'Confirm Damage')}
      </Button>
    );
  }

  // FIXME: Update all instances of this component to remove `<RenderWithAccess .../>` wrapper
  return <RenderWithAccess rules={[DAMAGE_CONFIRM_RULE]}>{render()}</RenderWithAccess>;
};
